<template>
  <div class="bitts-simple-tree-filter">
    <BittsInput
      v-if="searchable"
      v-model="searchQuery"
      :placeholder="placeholder"
      name="tree_filter_search"
      type="search"
      class="mb-8"
      @update:model-value="() => emit('search-applied', searchQuery)"
    />
    <div v-if="!loading">
      <div class="max-h-[270px] min-w-[244px] overflow-y-auto mb-8">
        <BittsBaseTree
          :node-data="filteredOptions"
          :expand-all="!!searchQuery"
          :initial-expanded-nodes="initialExpandedNodes"
          @update="onTreeUpdate"
        />
      </div>
      <div
        v-if="searchQuery && !filteredOptions.length"
        class="py-8 text-center"
      >
        <BittsEmptyState
          :font-awesome-icon="['fak', 'search']"
          :font-awesome-icon-style="{ height: '26px', width: '26px' }"
          font-awesome-icon-class="bitts-simple-tree-filter__empty-state-icon"
          title="No results"
          description="Try a different search term to find fields"
          class="w-[244px] border-none"
        />
      </div>
    </div>
    <div v-else class="py-8 flex justify-center w-[244px]">
      <FontAwesomeIcon
        :icon="['fak', 'loading']"
        spin
        class="text-neutral-accent w-28 h-28"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';

import BittsEmptyState from '../../BittsEmptyState/BittsEmptyState.vue';
import BittsInput from '../../BittsInput/BittsInput.vue';
import BittsBaseTree, { TreeNodeType } from '../../BittsTree/BittsBaseTree.vue';

const {
  options = [],
  value = [],
  initialExpandedNodes = {},
  searchable = true,
  placeholder = 'Search for a value',
  search = '',
  loading = false,
} = defineProps<{
  options: TreeNodeType[];
  value?: string[];
  initialExpandedNodes?: Record<string, boolean>;
  searchable?: boolean;
  placeholder?: string;
  search?: string;
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'search-applied', query: string): void;
  (e: 'filter-changed', filterValue: string[]): void;
}>();

const searchQuery = ref('');
const filterValue = ref<string[]>([...value]);

// Filter tree data based on search query and apply internal checked state
const filteredOptions = computed(() => {
  function applyCheckedState(node: TreeNodeType): TreeNodeType {
    const checked = filterValue.value.includes(node.id as string);
    return {
      ...node,
      checked,
      children: node.children
        ? node.children.map(applyCheckedState)
        : undefined,
    };
  }

  function filterNode(node: TreeNodeType): TreeNodeType | null {
    const matchesSearch = node.label
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase());

    if (node.children) {
      const filteredChildren = node.children
        .map(filterNode)
        .filter((child): child is TreeNodeType => child !== null);

      if (filteredChildren.length > 0 || matchesSearch) {
        return applyCheckedState({
          ...node,
          children: filteredChildren,
        });
      }
    } else if (matchesSearch) {
      return applyCheckedState(node);
    }

    return null;
  }

  const dataToFilter = options.map(applyCheckedState);

  if (!searchQuery.value) return dataToFilter;

  return dataToFilter
    .map(filterNode)
    .filter((node): node is TreeNodeType => node !== null);
});

function onTreeUpdate(tree: TreeNodeType[]) {
  // Extract checked IDs from the tree
  const newCheckedIds: string[] = [];

  function collectCheckedIds(nodes: TreeNodeType[]) {
    nodes.forEach((node) => {
      if (node.checked) {
        newCheckedIds.push(node.id as string);
      }
      if (node.children) {
        collectCheckedIds(node.children);
      }
    });
  }

  collectCheckedIds(tree);
  filterValue.value = newCheckedIds;
  emit('filter-changed', filterValue.value);
}

watchEffect(() => {
  searchQuery.value = search;
});

// Update internal state when props change
watchEffect(() => {
  filterValue.value = [...value];
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-simple-tree-filter {
  @apply px-16 pt-8;
}

:deep(.c-bitts-empty-state-large) {
  @apply p-8 pb-0;
}

:deep(.bitts-simple-tree-filter__empty-state-icon) {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}

:deep(.c-bitts-input .c-bitts-input__input .ant-input-clear-icon) {
  @apply hidden;
}

:deep(.ant-tree) {
  @apply bg-transparent;
}

:deep(.ant-tree-node-content-wrapper) {
  @apply h-[29px] flex items-center;
}

:deep(.ant-tree-node-content-wrapper:hover) {
  @apply bg-neutral-50;
}

:deep(.ant-tree-treenode) {
  @apply mb-0;
}
</style>
